import React, { useState } from 'react'
import reactStringReplace from 'react-string-replace'
import { Tooltip } from 'react-tippy'
import styled from 'styled-components'

import { CloseButton } from '../../components/buttons/Buttons'
import { Text } from '../../components/topography'
import { palette, mediaQueries, fonts } from '../../styles/variables'
import { translate } from '../../utils/translation'
import Icon from '../../components/icon/Icon'
import arrowLeft from '../../styles/assets/arrows/arrow_left.svg'

export const SearchContainer = styled.div({
  marginBottom: 20,
  boxShadow: 'inset 0px 75px 15px -7px rgba(19,23,35,0.2)',
  backgroundColor: palette.lightGray20,
})

export const ProgressBarContainer = styled.div({
  height: 64,
  width: '100%',
  display: 'flex',
  alignItems: 'flex-start',
  flexDirection: 'column',
  padding: '0 16px',
  [mediaQueries.mobile]: {
    padding: '0 20px',
  },
})

export const RecrawlWrapper = styled.div({
  display: 'flex',
  alignItems: 'center',
})

export const LastCrawlDate = styled.div({
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'space-between',
  alignItems: 'flex-end',
  fontSize: '13px',
  whiteSpace: 'pre-wrap',
  marginRight: '20px',
  lineHeight: '20px',
  textAlign: 'right',
  color: palette.darkGray50,
  '& b': {
    color: palette.darkGray,
  },
  [mediaQueries.mobile]: {
    flexDirection: 'row',
    justifyContent: 'flex-start',
  },
})

const Wrapper = styled.div({
  position: 'relative',
  display: 'inline',
})

const Container = styled.div((props) => ({
  width: props.width || '600px',
  color: palette.white,
  display: 'flex',
  padding: '10px',
  textAlign: 'left',
}))

const Section = styled.div(() => ({
  flex: 1,
  display: 'flex',
  flexDirection: 'column',
  padding: '5px 10px 15px 0px',
  ':nth-child(2)': {
    paddingLeft: '20px',
  },
}))

const SectionTitle = styled.span({
  fontFamily: fonts.primaryBold,
  textTransform: 'uppercase',
  color: palette.orange,
  fontWeight: 'bold',
  lineHeight: '40px',
  fontSize: 16,
})

const SectionText = styled.span({
  whiteSpace: 'pre-wrap',
  fontWeight: '300',
  fontSize: 12,
  color: palette.darkGray,
  lineHeight: '20px',
  textTransform: 'none',
  '& > a': {
    textDecoration: 'underline',
    color: palette.orange,
    fontFamily: fonts.primaryBold,
  },
  '& b': {
    fontFamily: fonts.primaryBold,
  },
})

const SubTextContainer = styled.span({
  fontSize: '14px',
  lineHeight: '14px',
  textTransform: 'initial',
  fontFamily: fonts.primaryBook,
  cursor: 'pointer',
  textDecoration: 'underline',
  [mediaQueries.mobile]: {
    marginTop: '5px',
    marginLeft: 0,
  },
})

export const SiteAuditErrorNote = (props) => {
  const { error } = props
  const troubleshootingTipsText = translate('troubleshooting_tips')
  const text = translate(`${error}_message`, troubleshootingTipsText)
  const [showTooltip, setShowTooltip] = useState(false)
  return (
    <Text fontSize="14px" lineHeight="14px !important">
      {reactStringReplace(text, troubleshootingTipsText, (match, index) => {
        return (
          <Wrapper
            key={index}
            onClick={(e) => {
              e.stopPropagation()
              setShowTooltip(true)
            }}
          >
            <Tooltip
              open={showTooltip}
              arrow
              position="bottom"
              theme="light"
              interactive
              sticky
              popperOptions={{
                modifiers: {
                  preventOverflow: {
                    enabled: true,
                  },
                  hide: {
                    enabled: true,
                  },
                },
              }}
              html={
                <Container width={error === 'unknown' ? '300px' : '600px'}>
                  {error !== 'unknown' && (
                    <Section>
                      <SectionTitle>{translate('what_is_this')}</SectionTitle>
                      <SectionText
                        dangerouslySetInnerHTML={{
                          __html: translate(`${error}_why`),
                        }}
                      />
                    </Section>
                  )}
                  <Section>
                    <SectionTitle>
                      {error === 'unknown'
                        ? translate('how_troubleshoot')
                        : translate('how_do_i_fix_it')}
                    </SectionTitle>
                    <SectionText
                      dangerouslySetInnerHTML={{
                        __html: translate(`${error}_how`),
                      }}
                    />
                  </Section>
                  <CloseButton
                    onClick={() => setShowTooltip(false)}
                    color={palette.darkGray}
                    styles={{
                      width: '24px',
                      height: '24px',
                      position: 'absolute',
                      top: '10px',
                      right: '5px',
                    }}
                  />
                </Container>
              }
            >
              <SubTextContainer>{troubleshootingTipsText}</SubTextContainer>
            </Tooltip>
          </Wrapper>
        )
      })}
    </Text>
  )
}

export const IssueHeader = styled.div({
  flex: 1,
  display: 'flex',
  alignItems: 'center',
  margin: '16px 0',
  fontSize: '14px',
  letterSpacing: '0.05em',
  textTransform: 'uppercase',
  flexWrap: 'wrap',
  [mediaQueries.mobile]: {
    marginTop: 0,
  },
})

const IconWrapper = styled.div({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'flex-start',
  marginBottom: '5px',
})
const IconText = styled.span({
  fontFamily: fonts.primaryBook,
  fontSize: '14px',
  lineHeight: '15px',
  color: palette.darkGray,
})

export const BackToProjectDomain = ({ backToProject }) => (
  <IconWrapper>
    <Icon
      src={arrowLeft}
      size="20px"
      margin="0 8px 0 0"
      style={{ cursor: 'pointer' }}
      onClick={backToProject}
    />
    <IconText>{translate('back_to_project_domain')}</IconText>
  </IconWrapper>
)

export const SubContainer = styled.div({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'flex-start',
  flexGrow: 1,
})
